import { t } from '@transifex/native';
import type { components } from '~/api/openapi';
import type { XpEventDto } from '~/lib/sse-client';
import { ToastLayout } from '../../toast-layout';
import { interpolateXp } from '../icons/xp-badge';

/** `XpEventDto` narrowed to the daily-login branch — only passed from `showXpEventToast`'s `daily-login` case. */
export type DailyLoginXpEvent = XpEventDto & {
  source: components['schemas']['DailyLoginSourceDto'];
};

type GamificationDailyLoginToastProps = {
  titleTemplate: string;
  description: string;
  xp: number;
  toastId?: string | number;
};

export function GamificationDailyLoginToast({
  titleTemplate,
  description,
  xp,
  toastId,
}: GamificationDailyLoginToastProps) {
  return (
    <ToastLayout
      title={interpolateXp(titleTemplate, xp)}
      icon={<img src="/assets/gamification/daily-login.svg" alt="" className="size-10 shrink-0" />}
      contentClassName="gap-xs"
      borderColor="border-green-500"
      toastId={toastId}
    >
      <p className="text-body-big whitespace-pre-line">{description}</p>
    </ToastLayout>
  );
}

export function getDailyLoginToastContent(event: DailyLoginXpEvent): {
  title: string;
  description: string;
} {
  const title =
    event.source.title.trim().length > 0
      ? event.source.title
      : t('Great job! {{XP}} earned today!');
  const description = resolveDailyLoginBody(event);

  return { title, description };
}

function resolveDailyLoginBody(event: DailyLoginXpEvent): string {
  const body = event.source.body.trim();
  if (body.length > 0) {
    return body;
  }

  const { dailyLoginAmount, challengeBonusAmount } = event.source.metadata;
  const bonusXp = event.stats.weeklyLoginChallengeConfig?.bonusXp ?? challengeBonusAmount;
  const requiredDays = event.stats.weeklyLoginChallengeConfig?.requiredLoginDays;
  const daysRemaining = Math.max((requiredDays ?? 0) - event.source.metadata.weeklyLoginDays, 0);

  if (bonusXp == null || requiredDays == null) {
    return t('Keep going with your daily login streak.');
  }

  if (daysRemaining <= 0) {
    return t(
      '{dailyXp}XP daily login + {bonusXp}XP weekly bonus.\nCome back next week to earn more XP!',
      {
        dailyXp: dailyLoginAmount,
        bonusXp,
      }
    );
  }

  return t(
    '{daysRemaining, plural, one {# more day to unlock {bonusXp} XP bonus} other {# more days to unlock {bonusXp} XP bonus}}',
    { daysRemaining, bonusXp }
  );
}
